import React from 'react';

import { Newsletter } from '../../../../.generated/Newsletter/NewsletterSignupModal.model';
import { NewsletterSignUpModal } from './NewsletterSignupModal';
import PlainTextWrapper from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { Themes } from 'lib/themes';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { Subscriptions } from 'lib/services/newsletter-signup-service';
import { useSitecorePage } from 'lib/hooks/sitecore/useSitecorePage';
import { Field } from '@sitecore-content-sdk/nextjs';

type NewsletterSignUpModalProps = Newsletter.NewsletterSignupModal.NewsletterSignupModal_Component;

type BasePageFieldsWithModal = {
  hideNewsletterSignupModal?: Field<boolean | string | number>;
  pageComponents?: {
    fields?: {
      hideNewsletterSignupModal?: Field<boolean | string | number>;
    };
  };
};

export const Default = (props: NewsletterSignUpModalProps) => {
  const { getDictionaryValue } = useDictionary();
  const siteSettings = useSiteSettings();
  const route = useSitecorePage();

  const toBoolean = (value: unknown): boolean => {
    if (value === true) return true;
    if (value === false || value == null) return false;

    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === '1' || normalized === 'true';
    }

    return Boolean(value);
  };

  const routeFields = route?.fields as BasePageFieldsWithModal | undefined;

  const rawHideNewsletterSignupModal =
    routeFields?.hideNewsletterSignupModal?.value ??
    routeFields?.pageComponents?.fields?.hideNewsletterSignupModal?.value ??
    (
      route as unknown as {
        fields?: {
          pageComponents?: {
            fields?: { hideNewsletterSignupModal?: { value: boolean | string | number } };
          };
        };
      }
    )?.fields?.pageComponents?.fields?.hideNewsletterSignupModal?.value;

  const hideNewsletterSignupModal = toBoolean(rawHideNewsletterSignupModal);
  const subscriptionId = siteSettings?.subscriptionID?.value;

  const subscriptions: Subscriptions[] | undefined = subscriptionId
    ? [
        {
          SubscriptionId: subscriptionId,
          Subscribed: true,
        },
      ]
    : undefined;

  if (!props.fields || hideNewsletterSignupModal) {
    return <></>;
  }

  return (
    <NewsletterSignUpModal
      theme={props.params?.theme as Themes}
      headline={
        <PlainTextWrapper
          field={props.fields?.headline}
          fallbacks={[
            {
              value: 'Sign up for our newsletter',
            },
          ]}
        />
      }
      description={
        <RichTextWrapper
          field={props.fields?.description}
          fallbacks={[
            {
              value: 'Stay up to date with our latest news.',
            },
          ]}
        />
      }
      emailLabel={
        <PlainTextWrapper
          field={props.fields?.emailLabel}
          fallbacks={[
            {
              value: 'Email address',
            },
          ]}
        />
      }
      emailPlaceholder={props.fields?.emailPlaceholderText?.value || 'Enter your email address'}
      requiredText={
        <PlainTextWrapper
          field={props.fields?.requiredLabel}
          tag="span"
          fallbacks={[
            {
              value: '* Required',
            },
          ]}
        />
      }
      submitButtonText={<PlainTextWrapper field={props.fields?.signupButtonText} tag="span" />}
      consentLabel={<RichTextWrapper field={props.fields?.legalText} />}
      loginLink={props.fields?.loginLink}
      loginPromptText={
        <PlainTextWrapper
          field={props.fields?.alreadyAccountText}
          tag="span"
          fallbacks={[
            {
              value: 'Already have an account?',
            },
          ]}
        />
      }
      successHeadline={
        <PlainTextWrapper
          field={props.fields?.successHeadline}
          tag="span"
          fallbacks={[
            {
              value: 'Thank you message',
            },
          ]}
        />
      }
      successDescription={<RichTextWrapper field={props.fields?.successDescription} />}
      successCtaText={
        <PlainTextWrapper
          field={props.fields?.successCTAText}
          tag="span"
          fallbacks={[
            {
              value: 'Log in',
            },
          ]}
        />
      }
      successSignupLink={(email: string) => {
        const linkField = props.fields?.successSignupLink;
        if (!linkField) return undefined;

        const fieldValue = linkField.value || linkField;
        const rawHref = (fieldValue?.href as string) || '';
        const rawUrl = (fieldValue?.url as string) || '';

        let baseHref: string;
        if (/^https?:\/\//i.test(rawHref)) {
          baseHref = rawHref;
        } else if (rawUrl) {
          baseHref = rawUrl;
        } else {
          baseHref = rawHref;
        }

        // Build final URL with email query param embedded directly in href
        const separator = baseHref.includes('?') ? '&' : '?';
        const finalHref = `${baseHref}${separator}email=${encodeURIComponent(email)}`;

        return {
          value: {
            ...fieldValue,
            href: finalHref,
          },
        };
      }}
      requiredErrorMessage={
        getDictionaryValue('Components.NewsletterSignupModal.RequiredEmail') ||
        'Email address is required.'
      }
      emailErrorMessage={
        getDictionaryValue('Components.NewsletterSignupModal.EmailValidation') ||
        'Please enter a valid email address.'
      }
      closeLabel={getDictionaryValue('Components.NewsletterSignupModal.CloseLabel') || 'Close'}
      subscriptions={subscriptions}
      newsletterTitle={props.fields?.headline?.value || 'Newsletter'}
      subscriberPosition="Interstitial"
    />
  );
};
